<template>
  <div>
    <div v-if="featuresLoading">
      <v-skeleton-loader
          type="list-item-avatar-two-line, divider, table-heading, list-item-two-line, table-heading, list-item-two-line"
          :elevation="3"
      ></v-skeleton-loader>
    </div>
    <div v-else>
      <v-card class="ti-widget overview-lease-widget-card-wrapper" :class="{'hb-border-warning' : leaseWithTenants.balance > 0 && is_closed, 'hb-elevation-medium' : elevation !== false}" v-if="leaseWithTenants.id && leaseWithTenants.id" :elevation="elevation === false ? 0 : null">
        <div class="v-card__header ti-widget__header mr-0" :class="[computedHeaderBackgroundColor]">
          <v-card-title class="ti-widget__title mr-0 pr-0 pb-0">
            <v-row class="ma-0 pa-0">
              <v-col :cols="parent !== 'space-profile' ? 10 : 12" class="ma-0 pa-0">
                <!-- BCT: Tenant Details in header for Space profile HB-909 -->
                <mini-profile-view 
                  v-if="contact && parent === 'space-profile' && !contact.national_account_id?.length"
                  :contact-id="contact.id"
                  :use-contact="false"
                  :contact-data="contact"
                />
                <hb-mini-profile  
                  v-else-if="contact && contact.national_account_id && parent === 'space-profile'" 
                  type="account" 
                  :title="contact.Business.name" 
                  :status="contact.status ? contact.status : undefined"
                  :address="computedBusinessAddress"
                  :id-verified="contact.email_verified ? contact.email_verified : undefined"
                  :account-space-count="calculateLength"
                  :account-space-type="contact.Leases && contact.Leases?.length === 1 ? contact?.Leases[0]?.Unit?.type : ''" 
                  :account-size="accountSize"
                  :account-total-monthly-spent="NationalAccountStat?.total_monthly_rent | formatMoney"
                />
                <HbHeaderInfo
                  v-else-if="unit"
                  :type="getUnitTypeLabel(unit)"
                  :title="unit.number ? unit.number : undefined"
                  :insurance="insurance"
                  :has-insurance="insuranceIsCurrent"
                  :insurance-name="insurance && insurance.name ? insurance.name : undefined"
                  :insurance-description="insurance && insurance.description ? insurance.description : undefined"
                  :status="leaseWithTenants && leaseWithTenants.Standing && leaseWithTenants.Standing.name ? leaseWithTenants.Standing.name : undefined"
                  :category-one="unit.label ? unit.label : undefined"
                  :category-two="unit.Category && unit.Category.name ? unit.Category.name : undefined"
                  :property="computedPropertyInfo"
                  :access-code="!is_closed && isSpaceAccessCodesVendor ? lease.Unit.pin : undefined"
                  :access-error="computedAccessHasError"
                  @pin="viewContact('access')"
                  row-max-width="350px"
                  @emitType="emitType"
                />
              </v-col>
              <v-col :cols="parent !== 'space-profile' ? 2 : 0" class="mx-0 mb-0 py-0 px-2 mt-n2">
                <hb-menu
                  v-if="(parent !== 'space-profile') && !is_closed && (leaseWithTenants && leaseWithTenants.Standing && leaseWithTenants.Standing.name && leaseWithTenants.Standing.name !== 'Pending')"
                  options
                  :optionsHoverType="emittedType"
                  align-right
                >
                  <v-list>
                    <v-list-item v-if="!isAuctionPaymentOrMovedOut && getConfigurationStatus(leaseWithTenants?.Unit?.unit_type_id,'have_invoices') && !nationalAccount" :disabled="isLoading($options.name)" @click="invoiceModal = true">
                        Create Invoice
                    </v-list-item>
                    <hb-tooltip v-else-if="nationalAccount">
                      <template v-slot:item>
                          <v-list-item disabled>
                            Create Invoice
                          </v-list-item>
                      </template>
                      <template v-slot:body>
                          Creation of invoice is not allowed for National Accounts.
                      </template>
                    </hb-tooltip>                    
                    <hb-tooltip v-else>
                      <template v-slot:item>
                          <v-list-item disabled>
                            Create Invoice
                          </v-list-item>
                      </template>
                      <template v-slot:body>
                        {{ isAuctionPaymentOrMovedOut ? 'This space has been auctioned' :  `Unit type '${getUnitTypeTitle(leaseWithTenants?.Unit)}' configurations are disabled. Please contact Administrator`}} 
                      </template>
                    </hb-tooltip>
                    <v-list-item :disabled="isLoading($options.name)" @click="$emit('transfer', leaseWithTenants)" v-if="!isAuctionPaymentOrMovedOut && !contact?.national_account_id && leaseWithTenants && leaseWithTenants.balance <= 0 && !openInvoices && checkPermissionProperties({label: 'transfer_lease', properties: [leaseWithTenants?.Unit?.property_id]}) && (!isTenantInDoNotRentList || hasPermission('move_in_do_not_rent'))">
                      Transfer
                    </v-list-item>
                    <hb-tooltip v-else>
                      <template v-slot:item>
                          <v-list-item disabled>
                            Transfer
                          </v-list-item>
                      </template>
                      <template v-slot:body>
                          <div v-if="isTenantInDoNotRentList && !hasPermission('move_in_do_not_rent')">
                            {{ TENANT.DNR.TRANSFER_TOOLTIP }}
                          </div>
                          <div v-else-if="isAuctionPaymentOrMovedOut">
                            This space has been auctioned
                          </div>
                          <div v-else-if="!checkPermissionProperties({label: 'transfer_lease', properties: [leaseWithTenants?.Unit?.property_id]})">
                            User is not allowed to perform transfer on this property
                          </div>
                          <div v-else>
                            Clear out balance to transfer unit
                          </div>
                      </template>
                    </hb-tooltip>
                    <v-list-item :disabled="isLoading($options.name)" @click="$emit('moveOut', lease)" v-if="checkPermissionProperties({label: 'move_out_lease', properties: [leaseWithTenants?.Unit?.property_id]}) || nationalAccount">
                      Move Out
                    </v-list-item>
                    <hb-tooltip v-else>
                      <template v-slot:item>
                        <v-list-item disabled>
                          Move Out
                        </v-list-item>
                      </template>
                      <template v-slot:body>
                        <div>
                          User is not allowed to perform move out on this property
                        </div>
                      </template>
                    </hb-tooltip>
                    <v-list-item v-if="!isAuctionPaymentOrMovedOut" :disabled="isLoading($options.name)" @click="$emit('merchandise', lease, 'merchandise')">
                      Sell Merchandise
                    </v-list-item>
                    <hb-tooltip v-else>
                      <template v-slot:item>
                          <v-list-item disabled>
                            Sell Merchandise
                          </v-list-item>
                      </template>
                      <template v-slot:body>
                          This space has been auctioned
                      </template>
                    </hb-tooltip>

                    <v-list-item v-if="!isAuctionPaymentOrMovedOut" :disabled="isLoading($options.name)" @click="$emit('merchandise', lease, 'fees')">
                      Add Fee
                    </v-list-item>
                    <hb-tooltip v-else>
                      <template v-slot:item>
                          <v-list-item disabled>
                            Add Fee
                          </v-list-item>
                      </template>
                      <template v-slot:body>
                          This space has been auctioned
                      </template>
                    </hb-tooltip>

                  </v-list>
                </hb-menu>

                <hb-menu
                  v-if="(parent !== 'space-profile') && is_closed && (leaseWithTenants && leaseWithTenants.Standing && leaseWithTenants.Standing.name && leaseWithTenants.Standing.name !== 'Pending')"
                  options
                  :optionsHoverType="emittedType"
                  align-right
                >
                  <v-list>
                    <v-list-item v-if="!nationalAccount" :disabled="isLoading($options.name)" @click="invoiceModal = true">
                        Create Invoice
                    </v-list-item>
                    <hb-tooltip v-else-if="nationalAccount">
                      <template v-slot:item>
                          <v-list-item disabled>
                            Create Invoice
                          </v-list-item>
                      </template>
                      <template v-slot:body>
                          Creation of invoice is not allowed for National Accounts.
                      </template>
                    </hb-tooltip> 
                    <v-list-item :disabled="isLoading($options.name)" v-if="leaseWithTenants && leaseWithTenants.balance > 0 && leaseWithTenants.OpenInvoices" @click="goPayment">
                      Take a Payment
                    </v-list-item>
                    <v-list-item v-else disabled>
                      Take a Payment
                    </v-list-item>                    
                    <v-list-item :disabled="isLoading($options.name)" v-if="leaseWithTenants && leaseWithTenants.balance > 0 && leaseWithTenants.OpenInvoices" @click="writeOffModal = true">
                      Write-Off Balance
                    </v-list-item>
                    <v-list-item v-else disabled>
                      Write-Off Balance
                    </v-list-item>
                    <v-list-item :disabled="isLoading($options.name)" v-if="hasPermission('reverse_moveout') && !isMoveOutThroughAution && isUnitAvailable && canReverseMoveOut" @click="reverseMoveOutModal = true">
                      Reverse Move-out
                    </v-list-item>
                    <template v-else>
                      <hb-tooltip v-if="tooltipToShow">
                        <template v-slot:item>
                          <v-list-item disabled>
                            Reverse Move-out
                          </v-list-item>
                        </template>
                        <template v-slot:body>
                          <div style="max-width: 250px;">
                            {{ tooltipToShow.message }}
                          </div>
                        </template>
                      </hb-tooltip>
                    </template>
                  </v-list>
                </hb-menu>
              </v-col>
            </v-row>
          </v-card-title>
        </div>
        <span v-if="leaseWithTenants.auction_status">
          <v-row class="no-gutters py-2 px-4 d-flex align-center" :class="{'hb-error-shade' : (leaseWithTenants.balance > 0 && is_closed) || emittedType && emittedType == 'error'}">
            <span class="font-weight-medium">Auction Manager</span>
            <VSpacer />
            <hb-tooltip v-if="auctionImageCount > 0">
              <template v-slot:item>
                <v-badge
                  :content="auctionImageCount > 99 ? '99+' : auctionImageCount"
                  :value="auctionImageCount > 0"
                  :color="'#F26500'"
                  overlap
                  offset-y="14"
                >
                  <HbIcon mdi-code="mdi-image" />
                </v-badge>
              </template>
              <template v-slot:body>
                {{ auctionImageCount }} Image<span v-if="auctionImageCount > 1">s</span>
              </template>
            </hb-tooltip>
            <hb-btn v-if="$route.name === 'ContactOverview'" :class="auctionImageCount > 99 ? 'pl-7' : 'pl-4'" small color="secondary" @click="$emit('view-auction')">View</hb-btn>
          </v-row>
          <VDivider />
        </span>
        <div v-if="is_loading" class="v-card__content ti-widget__content">
          <v-card class="ti-widget" style="min-height:700px">
            <v-row justify="center" align="center">
              <v-col class="text-center ma-12">
                <v-progress-circular width="2" size="32" indeterminate color="primary" ></v-progress-circular>
              </v-col>
            </v-row>
          </v-card>
        </div>
        <div v-else class="v-card__content ti-widget__content">
          <v-row
            v-if="isSpaceAccessCodesVendor && !is_closed && (lease.Unit.soft_catch === 1 || lease.Unit.late_catch === 1 || lease.Unit.hard_catch === 1)"
            class="accessRestrictionsWrapper px-4 py-3"
            no-gutters
          >
            <v-col cols="5" class="font-weight-medium">
              Access Restrictions
            </v-col>
            <v-spacer></v-spacer>
            <v-col cols="7">
              <v-row v-if="lease.Unit.soft_catch" no-gutters justify="end" style="padding: 2px 0 4px;">
                <hb-status-general status="Access Alert"></hb-status-general>
              </v-row>
              <v-row v-if="lease.Unit.late_catch || lease.Unit.hard_catch" no-gutters justify="end" style="padding: 2px 0 4px;">
                <hb-status-general v-if="lease.Unit.hard_catch" status="Manual Suspension"></hb-status-general>
                <hb-status-general v-else-if="lease.Unit.late_catch" status="Delinquency Suspension"></hb-status-general>
              </v-row>
            </v-col>
          </v-row>
          <WriteOff
            v-model="writeOffModal"
            :writeOffModal="writeOffModal"
            :lease="leaseWithTenants"
            :unit="leaseWithTenants?.Unit"
            :contact="contact"
            :auction="leaseWithTenants.LeaseAuction"
          >
          </WriteOff>
          <ReverseMoveOut
            v-model="reverseMoveOutModal"
            @processReverseMoveOut="processReverseMoveOut"
            :loader="ReverseMoveOutLoader"
          >
          </ReverseMoveOut>
          <!-- // BCT: Component separated from OverviewLeaseWidget -->
          <OverviewLeaseDetails
            :unit="unit"
            :lease="lease"
            :leaseWithTenants="leaseWithTenants"
            :invoice="invoice"
            :offset="offset"
            :services="services"
            :credits="credits"
            :contact="contact"
            :nationalAccount="nationalAccount"
            @nextPeriod="nextPeriod"
            @prevPeriod="prevPeriod"
            @insuranceDialogClose="insuranceDialogClose"
            @auctionSale="$emit('auctionSale', leaseWithTenants)"
            @processPayment="$emit('processPayment', leaseWithTenants.LeaseAuction?.payment_id)"
            @refundAuction="$emit('refundAuction', leaseWithTenants.LeaseAuction)"
            >
          </OverviewLeaseDetails>

          <div  v-if="parent === 'space-profile'">
            <v-divider></v-divider>
            <OverviewLeaseSettings
              :unit="unit"
              :lease="lease"
              :leaseWithTenants="leaseWithTenants"
              :invoice="invoice"
              :services="services"
              :contact="contact"
              :nationalAccount="nationalAccount"
              :payment_methods="payment_methods"
              :vehicleData="selectedVehicle"
              :autoPayData="autoPaymentData"
              :promotions="promotionsData"
              @fetchLease="fetchLease"
              >
            </OverviewLeaseSettings>
          </div>
          <!-- BCT: Component separated from OverviewLeaseWidget // -->

          <v-expansion-panels v-else class="hb-expansion-panel" v-model="more_settings" multiple flat>
            <v-expansion-panel>
              <v-divider></v-divider>
              <v-expansion-panel-header class="px-4 hb-lease-dropdown-header" :class="more_settings.includes(0) ? 'hb-link': 'hb-cloud-light'">
                <span class="hb-font-body-medium hb-text-night"> <HbIcon mdi-code="mdi-settings" small color="black" class="pr-2"/> {{is_closed ? 'More Information' : 'Space Settings & Information'}}</span>
              </v-expansion-panel-header>
              <!-- <v-divider v-if="more_settings.includes(0)"></v-divider> -->
              <v-expansion-panel-content class="pa-0 ma-0">
              <OverviewLeaseSettings
                :unit="unit"
                :lease="lease"
                :leaseWithTenants="leaseWithTenants"
                :invoice="invoice"
                :services="services"
                :contact="contact"
                :payment_methods="payment_methods"
                :vehicleData="selectedVehicle"
                :autoPayData="autoPaymentData"
                :promotions="promotionsData"
                :nationalAccount="nationalAccount"
                @fetchLease="fetchLease"
                >
              </OverviewLeaseSettings>
                <div class="d-flex justify-end my-3 mr-3" v-if="(parent !== 'space-profile') && !is_closed && (leaseWithTenants && leaseWithTenants.Standing && leaseWithTenants.Standing.name && leaseWithTenants.Standing.name !== 'Pending')">

                  <!-- put create invoice button in later -->
                  <hb-btn v-if="!isAuctionPaymentOrMovedOut && getConfigurationStatus(leaseWithTenants?.Unit?.unit_type_id,'have_invoices') && !nationalAccount" color="secondary" small :disabled="isLoading($options.name)" @click="invoiceModal = true" class="mr-3">Create Invoice</hb-btn>
                  <div v-if="!isAuctionPaymentOrMovedOut && !nationalAccount"">
                    <hb-btn class="mr-3" color="secondary" small :disabled="isLoading($options.name)" @click="$emit('transfer', lease)" v-if="leaseWithTenants && leaseWithTenants.balance <= 0 && !openInvoices && checkPermissionProperties({label: 'transfer_lease', properties: [leaseWithTenants?.Unit?.property_id]}) && (!isTenantInDoNotRentList || hasPermission('move_in_do_not_rent'))">Transfer</hb-btn>
                    <v-tooltip bottom v-else-if="isTenantInDoNotRentList && !hasPermission('move_in_do_not_rent')">
                      <template v-slot:activator="{ on }">
                          <span v-on="on">
                              <hb-btn class="mr-3" small color="secondary" disabled style="cursor: pointer;">Transfer</hb-btn>
                          </span>
                      </template>
                      <span>{{ TENANT.DNR.TRANSFER_TOOLTIP }}</span>
                    </v-tooltip>
                    <v-tooltip bottom v-else-if="!checkPermissionProperties({label: 'transfer_lease', properties: [leaseWithTenants?.Unit?.property_id]})">
                      <template v-slot:activator="{ on }">
                                          <span v-on="on">
                                              <hb-btn class="mr-3" small color="secondary" disabled style="cursor: pointer;">Transfer</hb-btn>
                                          </span>
                      </template>
                      <span>User is not allowed to perform transfer on this property</span>
                    </v-tooltip>
                    <v-tooltip bottom v-else>
                      <template v-slot:activator="{ on }">
                                          <span v-on="on">
                                              <hb-btn class="mr-3" small color="secondary" disabled style="cursor: pointer;">Transfer</hb-btn>
                                          </span>
                      </template>
                      <span>Clear out balance to transfer unit</span>
                    </v-tooltip>
                  </div>
                  <v-tooltip bottom v-if="(!checkPermissionProperties({label: 'move_out_lease', properties: [leaseWithTenants?.Unit?.property_id]})) && !nationalAccount">
                    <template v-slot:activator="{ on }">
                                        <span v-on="on">
                                            <hb-btn class="mr-3" small color="secondary" disabled style="cursor: pointer;">Move Out</hb-btn>
                                        </span>
                    </template>
                    <span>User is not allowed to perform move out on this property</span>
                  </v-tooltip>
                  <hb-btn color="secondary" small :disabled="isLoading($options.name)" @click="$emit('moveOut', leaseWithTenants)" v-else>Move Out</hb-btn>
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>

        </div>
        <CreateInvoiceModal 
          v-model="invoiceModal" 
          v-if="invoiceModal" 
          :contact="contact" 
          :lease="leaseWithTenants" 
          :property_id="leaseWithTenants?.Unit?.property_id" 
          @close="invoiceModal = false"
        ></CreateInvoiceModal>

        <modal v-if="showEditBreakdownModal" size="lg" allow-click-away="true" @close="showEditBreakdownModal = false" >
          <h3 slot="header">Setup Auto Pay</h3>
          <edit-breakdown
            :paymentMethods="payment_methods"
            :rent="leaseWithTenants.rent"
            :lease_id="leaseWithTenants.id"
            slot="body"
            @close="showEditBreakdownModal = false"
            @refetch="fetchLeasePaymentMethods" 
          >
        
          </edit-breakdown>
        </modal>
      </v-card>
    </div>
    <hb-modal
      title="Update Billing Setting"
      size="medium"
      confirmation
      v-model="show_update_billing_modal"
      v-if="show_update_billing_modal"
      @close="cancelInvoice"
      show-help-link
    >
      <template v-slot:content>
        <div class="px-6 py-4" v-if="bill_day != leaseWithTenants.bill_day">This space now has a balance due. If you skip the payment an invoice will be automatically generated and sent to the tenant.</div>
        <div class="px-6 py-4" v-if="bill_day != leaseWithTenants.bill_day">Would you like to take a payment now? </div>
        <div class="px-6 py-4" v-if="bill_day == leaseWithTenants.bill_day && leaseWithTenants.invoice_send_day != invoice_send_day">It will create invoice for the next billing period if the invoice creation date has passed according to new billing settings.</div>
        <div class="px-6 py-2" v-if="bill_day != leaseWithTenants.bill_day && leaseWithTenants.invoice_send_day != invoice_send_day">It will create invoice for the next billing period along with the stop gap invoice if the invoice creation date has passed according to new billing settings.</div>
      </template>
      <template v-slot:actions v-if="bill_day != leaseWithTenants.bill_day || invoice_send_day == leaseWithTenants.invoice_send_day">
        <hb-btn color="secondary" class="pr-4" v-if="hasPermission('bill_day_change_skip_payment')"  @click="billingOnPayment(true)">Skip Payment</hb-btn>
          <hb-tooltip v-else>
              <template v-slot:item >
                  <hb-btn color="secondary" class="pr-4" disabled>Skip Payment</hb-btn>
              </template>
              <template v-slot:body> You do not have the permission to skip payment </template>
          </hb-tooltip>
        <hb-btn color="primary" @click="showTakePaymentModal">Take a Payment</hb-btn>
      </template>
      <template v-slot:actions v-else>
        <hb-btn color="primary" class="pr-4" v-if="invoice_send_day != leaseWithTenants.invoice_send_day"  @click="saveInvoiceSendDay()">Save</hb-btn>
      </template>
    </hb-modal>
    <BillingOnTakePaymentModal
      v-model="show_take_payment_modal"
      v-if="show_take_payment_modal"
      :contact="contact"
      :lease="leaseWithTenants"
      @close="resetBillingOnField"
    >
    </BillingOnTakePaymentModal>
  </div>

</template>

<script type="text/babel">
    import Modal from '../assets/Modal.vue';
    import moment from 'moment';
    import EditServiceDialog from './EditServiceDialog.vue';
    // import EditBreakdown from './EditBreakdown.vue';
    import { EventBus } from '../../EventBus.js';
    import Status from '../includes/Messages.vue';
    import api from '../../assets/api.js';
    import CreateInvoiceModal from './CreateInvoiceModal.vue';
    import EditPayment from '../payments/EditPayment.vue';
    import BillingOnTakePaymentModal from './billingOn/BillingOnTakePaymentModal.vue';
    import { mapGetters, mapMutations, mapActions } from 'vuex';
    import OverviewLeaseDetails from './OverviewLeaseDetails.vue'
    import VEHICLE from '@/constants/vehicle.js'; 
    import LEASE from '@/constants/lease.js'; 
    import OverviewLeaseSettings from './OverviewLeaseSettings.vue'
    import WriteOff from '../includes/WriteOff/Index.vue'
    import { calendarMixin } from '../../mixins/calendarMixin';
    import { notificationMixin } from  '../../mixins/notificationMixin.js';
    import ReverseMoveOut from '../includes/ReverseMoveOutModal.vue';
    import propertyCurrentDateMixin from '../../mixins/propertyCurrentDateMixin.js';
    import MiniProfileView from '../includes/LeadIntake/MiniProfileView.vue';
    import TENANT from "@/constants/tenant.js";
    import contactFlagsMixin from "@/mixins/contactFlagsMixin.js";

    export default {
        name: "OverviewLeaseWidget",
        emits: ['takeAction',],
        data() {
            return {
              featuresLoading: true,
              invoiceModal: false,
              addPaymentMethod: false,
              is_auto_pay_after_billing_date: false,
              activelyEditing: '',
              paid_through_date: null,
              prepaid_balance: 0,
              showAction: 'false',
              contact_id: '',
              autoPaymentMethods:[],
              autoPaymentData:{},
            //  paymentMethods:[],
              availablePaymentMethods:[],
              notes: [],
              contactDrawer: false,
              // maintenance_id: '',
              // viewMaintenanceModal: false,
              fromLeaseWidget: true,
              // showEditBreakdownModal: false,
              access: [],
              tenants: [],
              // maintenance: [],
              services: [],
              insurance: [],
              discount: [],
              promotionsData: {},
              selectedVehicle: {},
              vehicle_types: VEHICLE.TYPES,
              editServiceDialog: false,
              selectedService: {},
              todaysDate: '',
              is_loading: false,
              unit: [],
              leaseWithTenants: {},
              more_settings: [],
              invoice: {},
              invoices:[],
              ledger:[],
              taxRate: 0,
              offset: 0,
              showCharges: false,
              hovering: {
                  insurance: false,
                  services: false,
                  vehicle: false
              },
              current_row_hovered: '',
              openInvoices: false,
              pageCreated: false,
              payment_id: '',
              showPaymentView: false,
              showView: false,
              credits: [],
              bill_day: 1,
              invoice_send_day: 0,
              prorate_invoice: [],
              prorate_days: 0,
              show_update_billing_modal: false,
              show_take_payment_modal: false,
              formErrors: '',
              disable_payment: false,
              writeOffModal: false,
              reverseMoveOutModal: false,
              isSpaceAccessCodesVendor: false,
              dummyUnitData: { 
                "soft_catch": 1,
                "late_catch": 1,
                "hard_catch": 1,
                "pin": "12321432",
                "unit_id": "2342343",
                "unit_number": "234",
                "unit_type": "storage",
              },
              emittedType: '',
              canReverseMoveOut: false,
              ReverseMoveOutLoader: false,
              auctionImageCount: 0,
            }
        },
        components:{
            Modal,
            Status,
            CreateInvoiceModal,
            EditPayment,
            BillingOnTakePaymentModal,
            OverviewLeaseDetails,
            OverviewLeaseSettings,
            WriteOff,
            ReverseMoveOut,
            MiniProfileView
        },
        // BCT: Added prop 'parent' for conditional based changes in Space profile.
        props: ['lease', 'contact', 'payment_methods', 'parent','propertyView','elevation', 'unitData','nationalAccount','hideAuctionManager','delinquencyId'],
        computed:{
            ...mapGetters({
                isAdmin: 'authenticationStore/isAdmin',
                isSuperAdmin: 'authenticationStore/isSuperAdmin',
                hasPermission: "authenticationStore/rolePermission",
                checkPermissionProperties: 'authenticationStore/checkPermissionProperties',
                template: 'onBoardingStore/template',
                unitLastClosedLease: 'onBoardingStore/getLastClosedLease',
                properties: 'propertiesStore/filtered',
                NationalAccountStat: 'onBoardingStore/getNationalAccount',
            }),
            autopayDays() {
              let days = Array(31)
                .fill(0)
                .map((n, i) => i );
              return days;
            },
          computedBusinessAddress() {
            if (
              this.contact.Business &&
              this.contact.Business.Address
            ) {
              const address_1 = this.contact.Business.Address.address
                ? this.contact.Business.Address.address
                : "";
              const address_2 = this.contact.Business.Address.address2
                ? this.contact.Business.Address.address2
                : "";

              let address = address_1;
              if (address_2 !== "") {
                if (address !== "") {
                  address += ", ";
                }
                address += address_2;
              }

              if (address !== "") {
                address += ", ";
              }

              address += this.$options.filters.formatAddress(this.contact.Business.Address);

              return address;
            } else {
              return undefined;
            }
          },
          calculateLength() {
            let leases = this.contact?.Leases.filter(lease => !lease.end_date)
            return leases?.filter((lease) => {
              return this.filterLeases(lease.Unit?.property_id);
            })?.length;
          },
          accountSize() {
            if (this.NationalAccountStat?.keystat.sqm) {
              return `${this.NationalAccountStat?.keystat.sqm} sqm`;
            }
            return '';
          },
            is_closed(){
                return moment(this.leaseWithTenants.end_date).startOf('day') <= moment().startOf('day');
            },
            
            nonInsuranceServices(){
                return this.services.filter(s => s.Product && s.Product.default_type !== 'insurance' && s.Product.default_type !== 'rent');
            },
            refundPayments(){
                /* need logic to send eligible refundable payments to refund modal */
                return [];
            },
            auctionCleaningDeposit() {
                return (this.leaseWithTenants.LeaseAuction && this.leaseWithTenants.LeaseAuction.cleaning_deposit && !this.leaseWithTenants.LeaseAuction.refund_id) ? this.leaseWithTenants.LeaseAuction.cleaning_deposit : 0;
            },
            insuranceIsCurrent(){
              let verifyInsurance = this.insurance && this.insurance.start_date && moment(this.insurance.start_date).subtract(8, 'hours').startOf('day') <= moment().startOf('day')
              if(verifyInsurance) {
                return !this.insurance.end_date || (this.insurance.end_date && moment(this.insurance.end_date).subtract(8, 'hours').startOf('day') >= moment().startOf('day'))
              }
            },
            isAuctionPaymentOrMovedOut() {
              return this.leaseWithTenants && this.leaseWithTenants.auction_status === 'move_out';
            },
            isMoveOutThroughAution(){
              return this.leaseWithTenants && this.leaseWithTenants?.auction_status === 'complete';
            },
            computedContactTitle() {
              return (this.contact.first ? this.contact.first : '') + (this.contact.middle ? ' ' + this.contact.middle : '') + (this.contact.last ? ' ' + this.contact.last : '');
            },
            computedContactPhone() {
              if(this.contact.Phones && this.contact.Phones[0] && this.contact.Phones[0].phone){
                return this.contact.Phones[0].phone;
              } else {
                return undefined;
              }
            },
            computedContactPhoneVerified() {
              if(this.contact.Phones && this.contact.Phones[0] && this.contact.Phones[0].phone_verified && this.contact.Phones[0].phone_verified == 1){
                  return true;
              } else {
                  return false;
              }
            },
            computedContactAddress() {
              if(this.contact.Addresses && this.contact.Addresses[0] && this.contact.Addresses[0].Address){
                const address_1 = this.contact.Addresses[0].Address.address ? this.contact.Addresses[0].Address.address : '';
                const address_2 = this.contact.Addresses[0].Address.address2 ? this.contact.Addresses[0].Address.address2 : '';

                let address = address_1;
                if(address_2 != '') address += ' ' + address_2;
                
                address += ', ' + this.$options.filters.formatAddress(this.contact.Addresses[0].Address); 
                
                return address;
              } else {
                return undefined;
              }
            },
            computedPropertyInfo() {
              if(this.unit && this.unit.Address){
                return this.$options.filters.formatAddress(this.unit.Address, '-', true);
              } else {
                return undefined;
              }
            },
            computedAccessHasError(){
              if(this.isSpaceAccessCodesVendor 
                    && 
                    (
                      this.access_control_data?.late_catch === 1 
                      || 
                      this.access_control_data?.hard_catch === 1
                    )
                ){
                  return true;
                } else {
                  return false;
                }
            },
            computedHeaderBackgroundColor(){
              if((this.leaseWithTenants.balance > 0 && this.is_closed) || this.emittedType && this.emittedType == 'error'){
                return 'hb-error-shade';
              } else if(this.emittedType && this.emittedType == 'caution'){
                return 'hb-caution-shade';
              } else if(this.emittedType && this.emittedType == 'success'){
                return 'hb-success-shade';
              } else if(this.emittedType && this.emittedType == 'guidance'){
                return 'hb-guidance-shade';
              } else {
                return '';
              }
            },
            computedRepresentativeName() {
                if (this.contact.Representative) {
                  return (this.contact?.Representative?.Contact?.first ? this.contact?.Representative?.Contact?.first : '') + (this.contact?.Representative?.Contact?.last ? ' ' + this.contact?.Representative?.Contact?.last : '');
                }
                return undefined;
            },
            isUnitAvailable(){
                return this.leaseWithTenants.end_date != null && this.leaseWithTenants?.Unit?.state && (this.leaseWithTenants?.Unit?.state?.toLowerCase() == 'available');
            },
            propertyCurrentDate(){
                return this.getPropertyDateTime(this.leaseWithTenants?.Unit?.property_id,this.properties);   
            },
            tooltipToShow() {
              return this.reverseMoveOutTooltipLookup();
            },
            isTenantInDoNotRentList() {
              return this.isFlagInList(TENANT.TENANT_FLAGS.DO_NOT_RENT, this.contact);
            },
        },
        async created (){
            this.TENANT = TENANT;
            if( this.parent ==  'space-profile') {
              this.more_settings = [0];
            }

            if(this.propertyView){
              await this.checkIsSpaceAccessCodesVendor(this.propertyView);
            }

            this.showPaymentView = false;

            await Promise.all([
              this.fetchLease(),
              this.fetchUnit()
            ]);
            
            this.fetchLeaseAutoPaymentMethods();
            
            this.fetchInsurance();
            this.fetchLeasePromotions();
            this.fetchServices();
            this.getCurrentServices();
            this.getVehicleData();
            this.fetchCredits();

            EventBus.$on('contact_updated', this.fetchLease);
            EventBus.$on('insurance_updated', this.fetchInsurance);
            EventBus.$on('service_updated', this.fetchServices);
            EventBus.$on('lease_payment_saved', this.getCurrentServices);
            EventBus.$on('payment_saved', this.getCurrentServices);
            EventBus.$on('invoice_saved', this.getCurrentServices);
            EventBus.$on('payment_method_deleted', this.paymentMethodRemoved);
            EventBus.$on('activeEdit', this.setActiveEditName);
            EventBus.$on('saveData', this.save);
            EventBus.$on('promotion_updated', this.fetchLeasePromotions);
            EventBus.$on('refetch_credit_payments', this.fetchCredits);
            EventBus.$on('fetch_unit_gate_status',this.refetchUnitInfo);
            EventBus.$on('lease_edited',this.refreshUnitLeaseData);
            EventBus.$on('auction-image-upload', this.fetchAuctionMediaCount);
            
            this.todaysDate = moment().startOf('day').format('YYYY-MM-DD');

            if(this.leaseWithTenants && this.leaseWithTenants.id && this.leaseWithTenants.bill_day) {
              this.bill_day = this.leaseWithTenants.bill_day;
            }

            if(this.leaseWithTenants && this.leaseWithTenants.id && this.leaseWithTenants.invoice_send_day) {
              this.invoice_send_day = this.leaseWithTenants.invoice_send_day;
            }
            if(this.leaseWithTenants.end_date && this.leaseWithTenants.end_date){
              await this.$store.dispatch('onBoardingStore/getTemplate',{configuration_unit:this.leaseWithTenants?.Unit, refetchTemplate:true});
              await this.$store.dispatch('onBoardingStore/findUnitLastClosedLease',{unit_id: this.leaseWithTenants?.Unit?.id});
              this.reverseMoveOutFunc();
            }
            if(this.leaseWithTenants && this.leaseWithTenants.lease_status){
              this.fetchAuctionMediaCount();
            }

        },
        mounted (){
          if (this.parent === 'space-profile') {
            this.$store.dispatch("onBoardingStore/getNationalAccountKeystat", {
              contact_id: this.contact.id,
              property_id: this.lease.Unit?.property_id
            });
          }
        },
        destroyed(){
            EventBus.$off('contact_updated', this.fetchLease);
            EventBus.$off('insurance_updated', this.fetchInsurance);
            EventBus.$off('service_updated', this.fetchServices);
            EventBus.$off('lease_payment_saved', this.getCurrentServices);
            EventBus.$off('payment_saved', this.getCurrentServices);
            EventBus.$off('invoice_saved', this.getCurrentServices);
            EventBus.$off('payment_method_deleted', this.paymentMethodRemoved);
            EventBus.$off('activeEdit', this.setActiveEditName);
            EventBus.$off('saveData', this.save);
            EventBus.$off('promotion_updated', this.fetchLeasePromotions);
            EventBus.$off('refetch_credit_payments', this.fetchCredits);
            EventBus.$off('fetch_unit_gate_status',this.refetchUnitInfo);
            EventBus.$off('lease_edited',this.refreshUnitLeaseData);
            EventBus.$off('auction-image-upload', this.fetchAuctionMediaCount);
        },
        filters:{

            getCCIcon(method){
                var icon = ( ['amex', 'visa', 'mastercard'].indexOf(method.card_type) >= 0 ) ? method.card_type: 'credit_card';
                return  '/img/cc_icons/64/'+ icon +'.png';
            },
        },
        methods: {
          ...mapActions({
            getTransformedPaymentObject: 'paymentsStore/getTransformedPaymentObject',
            NationalAccountKeyStat: 'onBoardingStore/getNationalAccount',
          }),
          ...mapMutations({
            setPaymentSource: "paymentsStore/setPaymentSource",
            createLeaseStructure: "paymentsStore/createLeaseStructure",
            setLeaseBilledMonths: "paymentsStore/setLeaseBilledMonths",
            setLeaseProperty: 'paymentsStore/setLeaseProperty',
            pushInvoices: 'paymentsStore/pushInvoices',
            resetPayments: 'paymentsStore/resetPayments',
            setPreSelectedLeaseId: 'paymentsStore/setPreSelectedLeaseId',
          }),
          filterLeases(property_id) {
            return this.properties.some((i) => i.id === property_id);
          },
          resetBillingOnField() {
            this.resetPayments();
            this.leaseWithTenants.bill_day = this.bill_day;
            this.leaseWithTenants.invoice_send_day = this.invoice_send_day;
            this.show_take_payment_modal = false;
            this.activelyEditing = '';
            this.prorate_invoice = [];
            this.prorate_days = 0;
            EventBus.$emit('reload_tenant_profile_leases');
          },
          async billingOnPayment(skipPayment = false) {
            if (this.hasPermission('bill_day_change_skip_payment')){
              try {
                let bill_day = this.leaseWithTenants.bill_day;
                let invoice_send_day = this.leaseWithTenants.invoice_send_day;
                let data = { dryrun: false, new_bill_day: bill_day, new_invoice_send_day: invoice_send_day }
                let r = await api.post(this, `${api.LEASES}${this.leaseWithTenants.id}/change-bill-day`, data);
                let description = '';
                if(skipPayment) {
                  description = `The billing day has been updated to ${this.$options.filters.dayEnding(bill_day)} of the month and an invoice has been generated and sent to the tenant.`;
                } else {
                  description = `The billing day has been updated to ${this.$options.filters.dayEnding(bill_day)} of the month.`
                }

                this.show_update_billing_modal = false;
                this.showMessageNotification({type: 'success', description: description});
                EventBus.$emit('activeEdit', '');
                EventBus.$emit('reload_tenant_profile_leases');
                this.activelyEditing = '';
                this.bill_day = this.leaseWithTenants.bill_day;
              } catch (err){
                this.showMessageNotification({description: err});
              }
            }else{
              this.showMessageNotification({ type: 'error', description: 'You do not have the permission to skip payment' });
            }
          },
            showTakePaymentModal() {
              this.show_update_billing_modal = false;
              this.show_take_payment_modal = true;
            },
            refetchUnitInfo(){
              this.fetchUnit();
            },
            async refreshUnitLeaseData(){
              await this.fetchLease();
              if(this.leaseWithTenants.end_date){
                await this.$store.dispatch('onBoardingStore/findUnitLastClosedLease',{unit_id: this.leaseWithTenants?.Unit?.id});
                this.reverseMoveOutFunc();
              }
            },
            async processReverseMoveOut(){
              try {
                  this.ReverseMoveOutLoader = true;
                  let r = await api.post(this, `${api.LEASES}/${this.leaseWithTenants.id}/reverse_move_out`);
                  EventBus.$emit('lease_edited');
                  EventBus.$emit('fetchLeaseData');
                  EventBus.$emit('contact_updated_transfer');
                  EventBus.$emit('reservation_made');
                  EventBus.$emit('delinquency_actions_update', {leases:[this.leaseWithTenants]});
                  EventBus.$emit('reload_tenant_profile_leases');
                  this.showMessageNotification({ type: 'success', description: 'Reverse move-out process has been completed!' });
                  this.ReverseMoveOutLoader = false;
                  this.reverseMoveOutModal = false;
                } catch (err) {
                  this.ReverseMoveOutLoader = false;
                  this.showMessageNotification({ type: 'error', description: err });
                }   
            },
            reverseMoveOutTooltipLookup() {
              // Add a new object here to include the new tooltip for the reverse move-out
              const reverseMoveOutTooltips = [ 
                { condition: !this.hasPermission('reverse_moveout'), message: LEASE.TOOLTIPS_MESSAGES.PERMISSION}, 
                { condition: this.isMoveOutThroughAution, message: LEASE.TOOLTIPS_MESSAGES.AUCTION_MOVE_OUT}, 
                { condition: !this.isUnitAvailable, message: LEASE.TOOLTIPS_MESSAGES.UNIT_AVAILABILITY}, 
                { condition: !this.canReverseMoveOut, message: LEASE.TOOLTIPS_MESSAGES.UNABLE_TO_REVERSE } 
              ];
              return reverseMoveOutTooltips.find((tooltip) => tooltip.condition);
            },
            async fetchPaymentMethods(pm){
              console.log("pm", pm)
              EventBus.$emit('payment_methods_updated');
              await this.setToAutoPay(pm)
              this.fetchLeaseAutoPaymentMethods();
            },
            showPayment(id) {
              this.payment_id = id;
              this.showPaymentView = true;
            },
            // NOTE: This function needs improvement 
            async save(prorate_invoice) {
              if(prorate_invoice?.lease_id && prorate_invoice.lease_id != this.leaseWithTenants.id && this.invoice_send_day != this.leaseWithTenants.invoice_send_day) return;

              try {
                if(this.is_auto_pay_after_billing_date) {
                  await api.put(this, `${api.LEASES}/${this.leaseWithTenants.id}/update`, {
                    auto_pay_after_billing_date: this.leaseWithTenants.auto_pay_after_billing_date
                  });
                  this.activelyEditing = '';
                  this.showMessageNotification({ type: 'success', description: 'AutoPay run day setting has been successfully updated.' });
                } else {
                  if(prorate_invoice?.lease_id == this.leaseWithTenants.id || this.invoice_send_day != this.leaseWithTenants.invoice_send_day) {
                    this.show_update_billing_modal = true;
                  } else {
                    if(this.bill_day == this.leaseWithTenants.bill_day && this.invoice_send_day == this.leaseWithTenants.invoice_send_day) {
                      this.activelyEditing = '';
                      return;
                    }
                    await this.billingOnPayment();
                  }
                }
              } catch (err) {}
            },
            async saveInvoiceSendDay() {
              try {
                await api.put(this, `${api.LEASES}/${this.leaseWithTenants.id}/update`, {
                  invoice_send_day: this.leaseWithTenants.invoice_send_day
                });
                this.show_update_billing_modal = false;
                EventBus.$emit('activeEdit', '');
                EventBus.$emit('reload_tenant_profile_leases');
                this.activelyEditing = '';
                this.invoice_send_day = this.leaseWithTenants.invoice_send_day;
                this.showMessageNotification({ type: 'success', description: 'Billing setting has been updated.' });
              } catch (err) {
                this.showMessageNotification({ type: 'error', description: 'Failed to update billing setting.' });
              }
            },
            goContact(tenant){
                this.$router.push('/contacts/' + tenant.contact_id);
            },
            editLease(){
                this.$emit('editLease');
            },
            async fetchLeaseAutoPaymentMethods(){
                let r = await api.get(this, api.LEASES + this.lease.id + '/payment-methods/autopay' )
                this.autoPaymentMethods = r.paymentMethods;       
                this.autoPaymentData = r         
            },
            async fetchUnit(){
              let r = await api.get(this, api.UNITS + this.lease.Unit?.id);
              this.unit = r.unit;
            }, 
            async setToAutoPay(paymentMethod){
                let r = await api.put(this, api.LEASES + this.lease.id + '/payment-methods/' + paymentMethod.id + '/autopay');
                this.fetchLeaseAutoPaymentMethods();
            },

            async removeAutoPay(paymentMethod, lease){
                let r = await api.delete(this, api.LEASES + this.lease.id + '/payment-methods/autopay');
                await this.fetchLeaseAutoPaymentMethods();
            },
            async fetchInsurance(){
                let r = await api.get(this, api.LEASES + this.lease.id + '/insurance');
                this.insurance = r.data.insurance_service;
            },
            async fetchLeasePromotions() {
              let r = await api.get(this, api.LEASES + this.lease.id + '/all-discounts');
              this.promotionsData = r;
            },
            async fetchServices(){
                let r = await api.get(this, api.LEASES + this.lease.id + '/services');
                this.services = r.services;
                this.is_loading = false;
            },
            async getVehicleData() {
                let r = await api.get(this, api.LEASES + this.lease.id + '/vehicles');
                if(!r.vehicles.id) {
                    this.selectedVehicle = {};
                    return;
                }
                this.selectedVehicle = r.vehicles;
            },
            async fetchLease(){
                this.is_loading = false;
                let r = await api.get(this, api.LEASES + this.lease.id + `?get_open_invoices=0`);
                this.leaseWithTenants = r.lease;
                this.featuresLoading = false
            },
            async fetchPayments() {
                let r = await api.get(this, api.LEASES + this.lease.id + '/credits');
                this.credits = r.payments.filter(payment => 
                    payment.credit_type === 'credit'
                );
            },
            async fetchCredits(payload){
                if(payload){
                  let { lease_id } = payload;
                  if(lease_id && lease_id != this.lease.id) return;
                }
                await api.get(this, api.LEASES + this.lease.id + '/credits').then(r => {
                    this.credits = r.payments.filter(payment => 
                        payment.credit_type === 'credit'
                    );
                });
            },

            insuranceDialogClose(){
                this.fetchInsurance();
            },
            async reloadServices(){
                this.is_loading = true;
                this.selectedService = {};
                this.showAddService = false;
                this.showEditService = false;
                await this.getCurrentServices();
                this.is_loading = false;

            },
            async cancelInvoice(){
              console.log("Now removing parent invoice");
              EventBus.$emit('activeEdit', '');
              this.show_update_billing_modal = false;
              this.leaseWithTenants.invoice_send_day = this.invoice_send_day;
            },
            async eventBusActions(){
                let s = await this.getCurrentServices();
                this.$nextTick(() => {
                    this.$forceUpdate();
                })
            },
            async getTaxRate(){
                let r = await api.get(this, api.SETTINGS_BILLING )
                this.taxRate = r.settings.taxRate;
            },
            async getCurrentServices(){
                var params = {
                    offset: this.offset
                };
                let r = await api.get(this, api.LEASES + this.lease.id + '/current-charges',  params );
                this.invoice = r.invoice;
            },
            async prevPeriod() {
                this.offset--;
                await this.getCurrentServices();
            },
            async nextPeriod(){
                this.offset++;
                await this.getCurrentServices();
            },
            goRefund(){
                this.$emit('takeAction', 'refund', this.refundPayments);
            },
            paymentMethodRemoved(id){
                if(this.autoPaymentMethods && this.autoPaymentMethods[0] && id && id === this.autoPaymentMethods[0].id){
                    this.autoPaymentMethods = [];
                }
            },
            setActiveEditName(name) {
              this.is_auto_pay_after_billing_date = (name == 'autopay') ?  true : false;
              this.activelyEditing = name;
              if(name == '') {
                this.resetPayments();
                this.leaseWithTenants.bill_day = this.bill_day;
                this.prorate_invoice = [];
              }
            },
            async checkIsSpaceAccessCodesVendor(property_id){
              if(property_id){
                  try {
                      let r = await api.get(this, api.PROPERTIES + property_id + '/access');
                      
                      if(r.property_access_name === 'Derrels'){
                          this.isSpaceAccessCodesVendor = true;
                      }
                  } catch (err){
                      console.log(err);
                  }
              }
            },
            viewContact(type){
              if(type === 'access'){
                  this.$router.push('/contacts/' + this.$route.params.contact_id + '/gate-access');
              } else if(type === 'payment-methods'){
                  this.$router.push('/contacts/' + this.$route.params.contact_id + '/payment_methods');
              }
            },
            goPayment(){

              this.setPreSelectedLeaseId(this.lease.id)

              EventBus.$emit('make_payment', {
                property_id: this.leaseWithTenants?.Unit?.property_id,
                contact_id: this.$route.params.contact_id
              });                
            },
            emitType(type){
              this.emittedType = type;
            },
            reverseMoveOutFunc() {
              this.canReverseMoveOut = false;
              
              const { unitLastClosedLease, propertyCurrentDate, template, leaseWithTenants } = this;
              const leaseId = leaseWithTenants?.id;
              const unitId = leaseWithTenants?.Unit?.id;
              
              // Check if there's a matching last closed lease
              if (!unitLastClosedLease || unitLastClosedLease.id !== leaseId) return;

              // Ensure the lease has an end date
              if (leaseWithTenants?.end_date === null) return;

              // Get the lease template for the unit, if available
              const leaseTemplate = template[unitId] || {};

              // Check if the lease template has reverse_move_out defined as 1
              if (!leaseTemplate.reverse_move_out) return;

              // Calculate the reverse move out day
              const currentDate = moment(propertyCurrentDate).format('YYYY-MM-DD');
              const reverseMoveOutDay = moment(leaseWithTenants.end_date).format('YYYY-MM-DD');

              // Check if the reverse move out day matches the current date
              if (reverseMoveOutDay === currentDate) {
                this.canReverseMoveOut = true;
              }
            },
            async fetchAuctionMediaCount(leaseId, count){
              if(leaseId && count && this.leaseWithTenants.id){
                if(leaseId === this.leaseWithTenants.id){
                  this.auctionImageCount = count;
                } else {
                  return;
                }
              } else if(this.leaseWithTenants.id) {
                let r = await api.get(this, api.AUCTIONS + '/leases/' + this.lease.id + '/uploads');
                if(r && r.imageSet && r.imageSet[0] && r.imageSet[0].uploads && r.imageSet[0].uploads.length){
                  this.auctionImageCount = r.imageSet[0].uploads.length;
                }
              } else {
                return;
              }
            },
            async fetchAccessControlMeta(){
              
              let r = await api.get(this, api.LEASES + this.lease.id + '/fetch-access-control-settings')
              this.access_control_data = r.access_control;
            }
        },
        
        mixins: [ calendarMixin, notificationMixin, propertyCurrentDateMixin, contactFlagsMixin ],
        watch: {
            async lease(){
              await Promise.all([
                this.fetchLease(),
                this.fetchUnit()
              ]);

              this.fetchLeaseAutoPaymentMethods();
              this.fetchInsurance();
              this.fetchLeasePromotions();
              this.fetchServices();
              this.getCurrentServices();
              this.getVehicleData();
              if(this.leaseWithTenants.end_date){
                await this.$store.dispatch('onBoardingStore/getTemplate',{configuration_unit:this.leaseWithTenants?.Unit, refetchTemplate:true});
                await this.$store.dispatch('onBoardingStore/findUnitLastClosedLease',{unit_id: this.leaseWithTenants?.Unit?.id});
                this.reverseMoveOutFunc();
              }
              if(this.leaseWithTenants.auction_status){
                this.fetchAuctionMediaCount();
              }
            }
        }
    }
</script>
<style lang="scss" scoped>
  .ti-widget {
    .ti-widget__header {
      padding: 14px 0px 16px;
      border-bottom: 1px solid rgba(0,0,0,0.1);
      .ti-widget__title {
        font-size: 1.2em;
        line-height: 1em;
        font-weight: bold;
        padding-top: 0px;
        padding-bottom: 5px;
      }
      .ti-widget__subtitle {
        opacity: 0.8;
        font-size: 0.9em;
        line-height: 1em;
        font-weight: normal;
        padding-top: 0px;
        padding-bottom: 0px;
      }
    }
  }
  .gray-color{
    color:#637381;
  }
  .lease-column-1{
    background:#F4F6F8;
  }
  .cc-icon {
    border: 1px solid #e2e2e2;
    border-radius: 2px;
    width: 64px;
  }
  .card-type{
    text-transform: uppercase;
    font-weight: 500;
  }
  .col-cursor-pointer{
    cursor:pointer;
  }

  .property-link{
    color:#306FB6;
    text-decoration:none;
  }

  .row-title {
    font-weight: 500;
  }

  .secondary-button{
    background: linear-gradient(180deg, #FFFFFF 0%, #F9FAFB 100%);
    border:1px solid #C4CDD5;
    box-shadow: 0px 1px 0px rgba(22, 29, 37, 0.05);
  }

  .hb-lease-dropdown-header {
    min-height: 40px !important;
  }

  .accessRestrictionsWrapper {
    background-color: #F9FAFB;
  }

  .overview-lease-widget-card-wrapper {
    border: 1px solid #DFE3E8; 
  }

  button.v-expansion-panel-header.hb-link{
    background: #e0f5f5 !important;
  }
</style>
